import { useRef } from "react";
import { useParallax } from "../hooks/useParallax";

/**
 * Pure-CSS/SVG "3D-ish" luxury piece for hero.
 * Slow rotating iso-shape with mouse-parallax and radial gradient rims.
 * Avoids R3F reconciler quirks and stays lightweight.
 */
export default function Hero3D({ className = "" }) {
  const wrapRef = useRef(null);
  const shapeRef = useRef(null);

  useParallax(wrapRef, shapeRef);

  return (
    <div
      ref={wrapRef}
      className={`relative ${className}`}
      aria-hidden="true"
      style={{ perspective: "1200px" }}
    >
      {/* halo */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(circle at 32% 32%, rgba(18,18,18,0.06), transparent 55%)",
        }}
      />
      {/* rotating rings */}
      <div
        ref={shapeRef}
        className="absolute inset-0 flex items-center justify-center hero3d-shape"
        style={{
          transform:
            "translate3d(var(--tx, 0), var(--ty, 0), 0) rotateX(18deg) rotateY(-12deg)",
          transformStyle: "preserve-3d",
          transition: "transform 0.6s cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <svg
          viewBox="0 0 400 400"
          className="w-[86%] h-[86%] animate-slow-spin"
          fill="none"
          stroke="currentColor"
          style={{ color: "#121212" }}
        >
          <defs>
            <radialGradient id="metal" cx="35%" cy="35%" r="70%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="45%" stopColor="#efece5" />
              <stop offset="100%" stopColor="#c9c3b6" />
            </radialGradient>
            <linearGradient id="rim" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#121212" stopOpacity="0.9" />
              <stop offset="60%" stopColor="#121212" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#121212" stopOpacity="0.7" />
            </linearGradient>
          </defs>
          {/* soft disc */}
          <circle cx="200" cy="200" r="150" fill="url(#metal)" />
          {/* rim rings */}
          <circle
            cx="200"
            cy="200"
            r="150"
            stroke="url(#rim)"
            strokeWidth="1.5"
            fill="none"
          />
          <ellipse
            cx="200"
            cy="200"
            rx="180"
            ry="60"
            stroke="rgba(18,18,18,0.35)"
            strokeWidth="0.8"
            fill="none"
          />
          <ellipse
            cx="200"
            cy="200"
            rx="180"
            ry="60"
            transform="rotate(60 200 200)"
            stroke="rgba(18,18,18,0.25)"
            strokeWidth="0.8"
            fill="none"
          />
          <ellipse
            cx="200"
            cy="200"
            rx="180"
            ry="60"
            transform="rotate(-60 200 200)"
            stroke="rgba(18,18,18,0.2)"
            strokeWidth="0.8"
            fill="none"
          />
          {/* inner tick marks */}
          {Array.from({ length: 36 }).map((_, i) => {
            const angle = (i * 10 * Math.PI) / 180;
            const inner = 132;
            const outer = i % 3 === 0 ? 148 : 142;
            return (
              <line
                key={`tick-${i}`}
                x1={200 + inner * Math.cos(angle)}
                y1={200 + inner * Math.sin(angle)}
                x2={200 + outer * Math.cos(angle)}
                y2={200 + outer * Math.sin(angle)}
                stroke="rgba(18,18,18,0.35)"
                strokeWidth={i % 3 === 0 ? 1.4 : 0.6}
              />
            );
          })}
          {/* center */}
          <circle cx="200" cy="200" r="6" fill="#121212" />
          <circle
            cx="200"
            cy="200"
            r="60"
            fill="none"
            stroke="rgba(18,18,18,0.35)"
            strokeWidth="0.8"
          />
        </svg>
      </div>
      {/* soft outer ring reversed */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: "rotateX(18deg) rotateY(-12deg)",
          transformStyle: "preserve-3d",
        }}
      >
        <div
          className="w-[110%] h-[110%] rounded-full"
          style={{
            border: "1px dashed rgba(18,18,18,0.18)",
            animation: "slow-spin 90s linear infinite reverse",
          }}
        />
      </div>
    </div>
  );
}
